<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import V2ReservationTablePicker from './V2ReservationTablePicker.vue';
  import V2ReservationTagsEditor from './V2ReservationTagsEditor.vue';
  import type { IReservationFormDraft } from '@/composition/reservations/use-reservation-form';
  import type { IReservation } from '@/interfaces/IReservation';
  import type { IReservationSlots } from '@/interfaces/IReservationSlots';
  import type { IReservationTablePickerInventory } from '@/interfaces/IReservationUi';
  import type { ISector } from '@/interfaces/ISector';

  const props = defineProps<{
    value: IReservationFormDraft;
    errors: Record<string, string>;
    inventory: IReservationTablePickerInventory | null;
    slots: IReservationSlots | null;
    durationMin: number;
    sectors: Pick<ISector, 'id' | 'name'>[];
    disabled: boolean;
    reservationToEdit?: IReservation;
  }>();
  const emit = defineEmits<{
    (event: 'input', value: IReservationFormDraft): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string) => String(proxy.$t(key));
  function patch<K extends keyof IReservationFormDraft>(
    key: K,
    value: IReservationFormDraft[K]
  ) {
    if (!props.disabled) emit('input', { ...props.value, [key]: value });
  }
  const email = computed({
    get: () => props.value.guest.email,
    set: (email: string) => patch('guest', { ...props.value.guest, email }),
  });
  const sector = computed({
    get: () => props.value.sector,
    set: (value: string) => patch('sector', value),
  });
  const duration = computed({
    get: () => props.value.duration,
    set: (value: string) => patch('duration', value),
  });
  const sourceItems = computed(() => [
    ...(props.reservationToEdit &&
    !['phone', 'staff'].includes(props.reservationToEdit.source)
      ? [
          {
            id: props.reservationToEdit.source,
            label: tr(`reservations.source.${props.reservationToEdit.source}`),
          },
        ]
      : []),
    { id: 'phone', label: tr('reservations.form.sourcePhone') },
    { id: 'staff', label: tr('reservations.form.sourceStaff') },
  ]);
  const tableItems = computed(() => [
    { id: 'auto', label: tr('reservations.form.tableAuto') },
    { id: 'manual', label: tr('reservations.form.tablePick') },
  ]);
  const durations = [60, 75, 90, 120, 150, 180];
  const occasions = [
    'birthday',
    'anniversary',
    'business',
    'date',
    'family',
    'other',
  ];
  const pickerStart = computed(
    () => props.value.startsAt || props.slots?.slots[0]?.startsAt || ''
  );
  function error(key: string) {
    return props.errors[key] ? tr(props.errors[key]) : undefined;
  }
  function setSource(value: string) {
    if (value === props.reservationToEdit?.source)
      patch('source', props.reservationToEdit.source);
    else if (value === 'phone' || value === 'staff') patch('source', value);
  }
  function setTables(value: string) {
    patch('manualTables', value === 'manual');
  }
</script>
<template>
  <section data-section="details" class="reservation-form-extra-details">
    <PosUiField
      :label="$t('reservations.form.email')"
      input-id="reservation-form-email"
      :error="error('email')"
      ><PosUiInput
        id="reservation-form-email"
        v-model="email"
        type="email"
        inputmode="email"
        :maxlength="128"
        :disabled="disabled"
    /></PosUiField>
    <PosUiField :label="$t('reservations.form.occasion')"
      ><div class="occasion-options">
        <PosUiButton
          v-for="occasion in occasions"
          :key="occasion"
          color="neutral"
          variant="outline"
          :class="{ active: value.occasion === occasion }"
          :aria-pressed="value.occasion === occasion"
          :disabled="disabled"
          @click="
            patch('occasion', value.occasion === occasion ? '' : occasion)
          "
          >{{ $t(`reservations.occasion.${occasion}`) }}</PosUiButton
        >
      </div></PosUiField
    >
    <PosUiField
      :label="$t('reservations.form.sector')"
      input-id="reservation-form-sector"
      ><select
        id="reservation-form-sector"
        v-model="sector"
        class="custom-input"
        :disabled="disabled"
      >
        <option value="">{{ $t('reservations.form.sectorAny') }}</option>
        <option
          v-for="sector in sectors"
          :key="sector.id"
          :value="String(sector.id)"
        >
          {{ sector.name }}
        </option>
      </select></PosUiField
    >
    <PosUiField :label="$t('reservations.form.table')" :error="error('tables')"
      ><PosUiSegmented
        :value="value.manualTables ? 'manual' : 'auto'"
        :items="tableItems"
        :disabled="disabled"
        @input="setTables" /><V2ReservationTablePicker
        v-if="pickerStart"
        v-show="value.manualTables"
        class="table-picker"
        :value="value.tableIds"
        @input="patch('tableIds', $event)"
        :party-size="value.partySize"
        :starts-at="pickerStart"
        :duration-min="durationMin"
        :exclude-reservation-id="
          reservationToEdit ? reservationToEdit.id : undefined
        "
        :inventory="inventory"
        :disabled="disabled"
    /></PosUiField>
    <PosUiField
      :label="$t('reservations.form.duration')"
      input-id="reservation-form-duration"
      ><select
        id="reservation-form-duration"
        v-model="duration"
        class="custom-input"
        :disabled="disabled"
      >
        <option value="">
          {{
            $t('reservations.form.durationAuto', {
              minutes: slots ? slots.durationMin : 90,
            })
          }}
        </option>
        <option
          v-if="
            reservationToEdit &&
            !durations.includes(reservationToEdit.durationMin)
          "
          :value="String(reservationToEdit.durationMin)"
        >
          {{
            $t('reservations.common.minutes', {
              minutes: reservationToEdit.durationMin,
            })
          }}
        </option>
        <option
          v-for="duration in durations"
          :key="duration"
          :value="String(duration)"
        >
          {{ $t('reservations.common.minutes', { minutes: duration }) }}
        </option>
      </select></PosUiField
    >
    <PosUiField :label="$t('reservations.form.source')"
      ><PosUiSegmented
        :value="value.source"
        :items="sourceItems"
        :disabled="disabled"
        @input="setSource"
    /></PosUiField>
    <PosUiField
      :label="$t('reservations.form.allergies')"
      input-id="reservation-form-allergies"
      ><PosUiTextarea
        id="reservation-form-allergies"
        :value="value.allergies"
        :rows="2"
        :maxlength="255"
        :disabled="disabled"
        @input="patch('allergies', $event)"
    /></PosUiField>
    <PosUiField
      :label="$t('reservations.form.guestNotes')"
      input-id="reservation-form-guest-notes"
      ><PosUiTextarea
        id="reservation-form-guest-notes"
        :value="value.notesGuest"
        @input="patch('notesGuest', $event)"
        :rows="2"
        :maxlength="10000"
        :disabled="disabled"
    /></PosUiField>
    <PosUiField
      :label="$t('reservations.form.internalNotes')"
      input-id="reservation-form-internal-notes"
      ><PosUiTextarea
        id="reservation-form-internal-notes"
        :value="value.notesInternal"
        @input="patch('notesInternal', $event)"
        :rows="2"
        :maxlength="10000"
        :disabled="disabled"
    /></PosUiField>
    <PosUiField :label="$t('reservations.form.tags')"
      ><V2ReservationTagsEditor
        :value="value.tags"
        @input="patch('tags', $event)"
        :disabled="disabled"
    /></PosUiField>
    <label v-if="reservationToEdit" class="switch-row"
      ><span>{{ $t('reservations.form.notify') }}</span
      ><PosUiSwitch
        :value="value.notifyGuest"
        @input="patch('notifyGuest', $event)"
        :disabled="disabled"
        :aria-label="$t('reservations.form.notify')"
    /></label>
  </section>
</template>
<style scoped lang="scss">
  .reservation-form-extra-details {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-5 tw-rounded-xl tw-bg-canvas tw-p-4;
    .custom-input {
      @apply tw-min-h-11 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900;
    }
    .occasion-options {
      @apply tw-flex tw-flex-wrap tw-gap-2;
      .active {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-white;
      }
    }
    .switch-row {
      @apply tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-sm tw-text-neutral-800;
    }
    :deep(.pos-ui-segmented-option) {
      @apply tw-min-h-11 tw-h-auto tw-whitespace-normal tw-py-2;
    }
    // PosUiField spaces its label only: the picker under the segmented
    // control keeps its own distance.
    .table-picker {
      @apply tw-mt-1.5;
    }
  }
</style>
